import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  getToolIconByToolkitName,
  getToolkitIconByName,
} from "@arcadeai/design-system/metadata/toolkit-icons";

import type { ProcessedTool } from "@arcadeai/ui-kit/arcade/arcade";

import { AlertTriangle, Key, Lock } from "lucide-react";
import { useMemo } from "react";
import { useToolRequirementsStatus } from "@/hooks/use-tool-requirements-status";
import { useToolkitPickerData } from "@/hooks/use-toolkit-picker-data";

const MAX_TOOLTIP_LINES = 12;
const MAX_TOOLS_PER_TOOLKIT = 2;

type LimitedGroup = { toolkit: string; tools: string[]; moreTools: number };

const limitGroups = (
  groups: Map<string, string[]>,
  toolkitNames: string[],
  maxLines: number,
  maxPerToolkit: number
): { groups: LimitedGroup[]; hasMore: boolean } => {
  let remaining = maxLines;
  let hasMore = false;
  const limited: LimitedGroup[] = [];

  for (let i = 0; i < toolkitNames.length; i += 1) {
    if (remaining <= 0) {
      hasMore = true;
      break;
    }

    const tk = toolkitNames[i];
    const toolsForTk = groups.get(tk) ?? [];

    // Reserve one line for the toolkit header
    remaining -= 1;
    if (remaining < 0) {
      hasMore = true;
      break;
    }

    const allowedCount = Math.min(Math.max(remaining, 0), maxPerToolkit);
    const allowedTools = toolsForTk.slice(0, allowedCount);
    const moreTools = toolsForTk.length - allowedTools.length;
    if (moreTools > 0) {
      hasMore = true;
    }

    limited.push({ toolkit: tk, tools: allowedTools, moreTools });
    remaining -= allowedTools.length;

    if (remaining <= 0 && i < toolkitNames.length - 1) {
      hasMore = true;
      break;
    }
  }

  return { groups: limited, hasMore };
};

type AllowedToolsCellProps = {
  tools: string[];
};

export const AllowedToolsCell = ({ tools }: AllowedToolsCellProps) => {
  const { data: toolkitCollection } = useToolkitPickerData();
  const { analyzeBatchRequirements } = useToolRequirementsStatus();

  // Resolve tool strings to ProcessedTool objects
  // Tool FQNs may include version suffixes (e.g., "Toolkit.Tool@1.0.0"),
  // so we strip the "@version" to match against the base name
  const toolObjects = useMemo<ProcessedTool[]>(() => {
    if (!toolkitCollection) {
      return [];
    }

    // Build a lookup map for O(1) access instead of O(n) per tool
    const toolMap = new Map(
      toolkitCollection.tools.map((t) => [
        t.fully_qualified_name.split("@")[0],
        t,
      ])
    );

    return tools
      .map((toolString) => toolMap.get(toolString))
      .filter((tool): tool is ProcessedTool => Boolean(tool));
  }, [tools, toolkitCollection]);

  // Analyze missing requirements for all tools
  const { missingRequirements } = useMemo(
    () => analyzeBatchRequirements(toolObjects),
    [analyzeBatchRequirements, toolObjects]
  );

  if (!tools.length) {
    return <span>—</span>;
  }

  const groups = tools.reduce((map, full) => {
    const parts = full.split(".", 2);
    const toolkit = parts[0];
    const toolName = parts.length > 1 ? parts[1] : "";
    if (toolkit && toolName) {
      const arr = map.get(toolkit) ?? [];
      arr.push(toolName);
      map.set(toolkit, arr);
    }
    return map;
  }, new Map<string, string[]>());

  const toolkitNames = Array.from(groups.keys());
  const MAX_VISIBLE_TOOLKITS = 3;
  const visible = toolkitNames.slice(0, MAX_VISIBLE_TOOLKITS);
  const overflowCount = Math.max(toolkitNames.length - visible.length, 0);

  const { groups: limited } = limitGroups(
    groups,
    toolkitNames,
    MAX_TOOLTIP_LINES,
    MAX_TOOLS_PER_TOOLKIT
  );
  const missingToolkits = Math.max(toolkitNames.length - limited.length, 0);

  return (
    <span className="inline-flex items-center gap-2">
      {/* Toolkit icons with their own tooltip */}
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex items-center" />}>
          {visible.map((tk, idx) => {
            const ToolkitIcon = getToolkitIconByName(tk);
            return (
              <span
                className={`relative first:ml-0 ${idx > 0 ? "-ml-2" : ""}`}
                key={tk}
                style={{ zIndex: idx + 1 }}
              >
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-xl bg-gradient-to-br from-white to-neutral-50 p-1.5 shadow-lg ring-1 ring-neutral-200/80 transition-all hover:scale-110 hover:shadow-xl dark:from-neutral-800 dark:to-neutral-900 dark:ring-neutral-700/80"
                  title={tk}
                >
                  <ToolkitIcon className="h-3 w-3 text-neutral-700 dark:text-neutral-300" />
                </span>
              </span>
            );
          })}
          {overflowCount > 0 ? (
            <span
              className="relative -ml-2 inline-flex h-6 w-6 items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 font-bold text-[11px] text-blue-800 shadow-lg ring-1 ring-blue-200/80 dark:from-blue-900 dark:to-blue-800 dark:text-blue-200 dark:ring-blue-700"
              style={{ zIndex: visible.length + 10 }}
            >
              +{overflowCount}
            </span>
          ) : null}
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={6}>
          <div className="text-xs">
            <ul className="space-y-2">
              {limited.map(
                ({ toolkit: toolkitName, tools: toolNames, moreTools }) => {
                  const ToolkitIconCmp = getToolkitIconByName(toolkitName);
                  const ToolIconCmp = getToolIconByToolkitName(toolkitName);
                  return (
                    <li key={toolkitName}>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-4 w-4 items-center justify-center">
                          <ToolkitIconCmp className="h-3 w-3" />
                        </span>
                        <span className="font-semibold text-neutral-800 dark:text-neutral-200">
                          {toolkitName}
                        </span>
                        {moreTools > 0 ? (
                          <span className="text-[11px] text-neutral-500">
                            +{moreTools} more
                          </span>
                        ) : null}
                      </div>
                      {toolNames.length > 0 ? (
                        <ul className="mt-1 ml-5 space-y-1">
                          {toolNames.map((tool) => (
                            <li
                              className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300"
                              key={`${toolkitName}-${tool}`}
                            >
                              <ToolIconCmp className="h-3 w-3" />
                              <span>{tool}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  );
                }
              )}
            </ul>
            {missingToolkits > 0 ? (
              <div className="mt-2 text-[11px] text-neutral-500">
                +{missingToolkits} more toolkits
              </div>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>

      {/* Warning badge with its own separate tooltip */}
      {missingRequirements.length > 0 && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Badge
                className="border-orange-200 bg-orange-100 text-orange-800 dark:border-orange-700 dark:bg-orange-900 dark:text-orange-200"
                variant="secondary"
              />
            }
          >
            <AlertTriangle className="mr-1 h-3 w-3" />
            {missingRequirements.length}
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={6}>
            <div className="text-xs">
              <div className="mb-2 flex items-center gap-2 text-orange-600 dark:text-orange-400">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-semibold">Missing Requirements</span>
              </div>
              <ul className="space-y-2">
                {missingRequirements.map((req) => (
                  <li
                    className="text-neutral-700 dark:text-neutral-300"
                    key={`${req.type}-${req.key}`}
                  >
                    <div className="flex items-center gap-1.5 font-medium">
                      {req.type === "secret" ? (
                        <Key className="h-3 w-3 text-zinc-500" />
                      ) : (
                        <Lock className="h-3 w-3 text-zinc-500" />
                      )}
                      <span>
                        {req.type === "oauth"
                          ? `OAuth: ${req.displayName}`
                          : req.displayName}
                      </span>
                    </div>
                    <div className="ml-5 text-[11px] text-neutral-600 dark:text-neutral-400">
                      {req.toolNames.length <= 2
                        ? `Required by: ${req.toolNames.join(", ")}`
                        : `Required by: ${req.toolNames.slice(0, 2).join(", ")} and ${req.toolNames.length - 2} other tool${req.toolNames.length - 2 === 1 ? "" : "s"}`}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );
};
